import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import TabSelect from 'components/forms/basefields/TabSelect';
import { useModal } from 'components/modals/ModalContext';
import JobModal from '../jobs/JobModal';
import { useSettings } from 'services/settings/SettingsContext';
import { formatEmailListDate } from 'services/utils/dateTimeUtils';
import '../../style/scss/email-list.scss';

type SmsListTab = 'scheduled' | 'sent';

// The tab options of the sms list
const tabOptions: { value: SmsListTab; label: string; icon: string }[] = [
    { value: 'scheduled', label: 'sms.general.tab_scheduled_label', icon: 'time' },
    { value: 'sent', label: 'sms.general.tab_sent_label', icon: 'email' },
];

const SmsList: React.FC = () => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { userLocale, userTimezone } = useSettings();
    const [activeTab, setActiveTab] = useState<SmsListTab>('scheduled');

    // Open the job modal of the clicked sms message
    const handleRowClick = (row: any) => {
        if (!row.job) return;
        initializeModal(<JobModal itemId={row.job} />, { modalSize: 'large' });
    };

    // Columns for the scheduled sms list
    const scheduledColumns: ListColumnType[] = useMemo(() => [
        { width: '1.5fr', field: 'recipient_name', label: 'sms.general.recipient_label' },
        { width: '1fr', field: 'job_number', label: 'sms.general.job_label' },
        {
            width: '1.5fr',
            field: 'message_type',
            label: 'sms.general.message_type_label',
            render: (_, row: any) => <>{t(`sms_template.message_type.${row.message_type}`)}</>,
        },
        {
            width: '1fr',
            field: 'scheduled_at',
            label: 'sms.general.scheduled_at_label',
            render: (_, row: any) => <>{formatEmailListDate(row.scheduled_at, userLocale, userTimezone)}</>,
            customClass: () => 'email-date-column',
        },
    ], [userLocale, userTimezone]);

    // Columns for the sent sms list
    const sentColumns: ListColumnType[] = useMemo(() => [
        { width: '1.5fr', field: 'phone_number', label: 'sms.general.phone_number_label' },
        { width: '1fr', field: 'job_number', label: 'sms.general.job_label' },
        { width: '3fr', field: 'body', label: 'sms.general.body_label' },
        {
            width: '0.8fr',
            field: 'status',
            label: 'sms.general.status_label',
            render: (_, row: any) => <>{t(`sms_message.status.${row.status}`)}</>,
        },
        {
            width: '1fr',
            field: 'sent_at',
            label: 'sms.general.sent_at_label',
            render: (_, row: any) => <>{formatEmailListDate(row.sent_at, userLocale, userTimezone)}</>,
            customClass: () => 'email-date-column',
        },
    ], [userLocale, userTimezone]);

    // Render the tab select at the far right of the list header
    const renderTabSelect = useCallback(() => (
        <TabSelect
            options={tabOptions}
            selectedOption={tabOptions.find(option => option.value === activeTab)}
            onChange={(value) => setActiveTab(value as SmsListTab)}
            optionFormat={(option) => option.label}
        />
    ), [activeTab]);

    return (
        <div className='email-inbox'>
            {activeTab === 'scheduled' && (
                <AbstractList
                    query={{
                        endpoint: 'get_sms_message_list',
                        object: 'sms_message',
                        defaultParams: { statuses: 'scheduled,retrying', deleted: false },
                    }}
                    objectName='sms.general.object_name'
                    columns={scheduledColumns}
                    showAddButton={false}
                    renderTrailingInfo={renderTabSelect}
                    isPaginated={true}
                    onRowClick={handleRowClick}
                />
            )}

            {activeTab === 'sent' && (
                <AbstractList
                    query={{
                        endpoint: 'get_sms_message_list',
                        object: 'sms_message',
                        defaultParams: { statuses: 'sent,failed,skipped', deleted: false },
                    }}
                    objectName='sms.general.object_name'
                    columns={sentColumns}
                    showAddButton={false}
                    renderTrailingInfo={renderTabSelect}
                    isPaginated={true}
                    onRowClick={handleRowClick}
                />
            )}
        </div>
    );
};

export default SmsList;
